<script setup lang="ts">
import { computed, inject } from 'vue'
import AdvancedSettings from './AdvancedSettings.vue'
import BinaryReferencePicker from './BinaryReferencePicker.vue'
import MemberNameField from './MemberNameField.vue'
import { referenceOf } from './binary-reference'
import { levelPositions, memberSuggestions, referencedNode } from './member-names'
import type { BinaryReference, FlattenLevel, FlattenServiceModel } from '@/types'
import { BBEL_SCOPE_KEY, type BbelScopeContext } from '@/views/builder/composables/bbel-context'

const props = defineProps<{
  modelValue: FlattenServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: FlattenServiceModel]
}>()

function update(changes: Partial<FlattenServiceModel>) {
  emit('update:modelValue', { ...props.modelValue, ...changes })
}

function updateSource(reference: BinaryReference) {
  update({ source: { $type: 'binary', reference } })
}

function updateLevel(index: number, changes: Partial<FlattenLevel>) {
  const levels = props.modelValue.levels.map((level, at) => (at === index ? { ...level, ...changes } : level))
  update({ levels })
}

function addLevel() {
  update({ levels: [...props.modelValue.levels, { path: [], flattenedKey: '', fields: [] }] })
}

function removeLastLevel() {
  update({ levels: props.modelValue.levels.slice(0, -1) })
}

// A Flatten always has one level, so the last can go only when another is left.
function canRemove(index: number): boolean {
  const isLast = index === props.modelValue.levels.length - 1
  return isLast && props.modelValue.levels.length > 1
}

// positions[n] is where level n looks for its array, and positions[n + 1] is that array.
// Level 0 looks at the position Source names; each level after it looks inside the array above.
const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const positions = computed(() => {
  const source = referencedNode(scopeCtx?.value.roots ?? [], referenceOf(props.modelValue.source))
  return levelPositions(source, props.modelValue.levels)
})
const arraySuggestions = (index: number) => memberSuggestions(positions.value[index], 'arrays')
const fieldSuggestions = (index: number) => memberSuggestions(positions.value[index + 1], 'names')
</script>

<template>
  <div>
    <div class="mb-3">
      <div class="form-label">Source</div>
      <BinaryReferencePicker
        label="Source"
        :model-value="referenceOf(modelValue.source)"
        @update:model-value="updateSource"
      />
    </div>

    <div class="mb-3">
      <div class="form-label">Levels</div>
      <!-- Column headings once, on a wide panel. On a narrow one each cell carries its own heading. -->
      <div class="row g-2 small text-body-secondary d-none d-md-flex mb-1" aria-hidden="true">
        <div class="col-md-4">Array</div>
        <div class="col-md-3">Row key</div>
        <div class="col-md-4">Fields</div>
      </div>
      <div v-for="(level, index) in modelValue.levels" :key="index" class="row g-2 mb-2">
        <div class="col-md-4">
          <div class="small text-body-secondary d-md-none">Array</div>
          <MemberNameField
            :label="`Array of level ${index + 1}`"
            placeholder="Array name"
            :model-value="level.path"
            :suggestions="arraySuggestions(index)"
            @update:model-value="updateLevel(index, { path: $event })"
          />
        </div>
        <div class="col-md-3">
          <div class="small text-body-secondary d-md-none">Row key</div>
          <input
            type="text"
            class="form-control"
            :value="level.flattenedKey"
            :aria-label="`Row key of level ${index + 1}`"
            autocomplete="off"
            @input="updateLevel(index, { flattenedKey: ($event.target as HTMLInputElement).value })"
          />
        </div>
        <div class="col-md-4">
          <div class="small text-body-secondary d-md-none">Fields</div>
          <MemberNameField
            :label="`Fields of level ${index + 1}`"
            placeholder="All fields"
            multiple
            :model-value="level.fields"
            :suggestions="fieldSuggestions(index)"
            @update:model-value="updateLevel(index, { fields: $event })"
          />
        </div>
        <div class="col-md-1 d-flex align-items-center">
          <button
            v-if="canRemove(index)"
            type="button"
            class="btn btn-link btn-sm p-0 text-danger"
            @click="removeLastLevel"
          >
            Remove
          </button>
        </div>
      </div>
      <button type="button" class="bb-btn-secondary-cta btn-sm" @click="addLevel">
        <i class="bi bi-plus"></i> Add level
      </button>
    </div>

    <AdvancedSettings />
  </div>
</template>
